import type { Dispatch, DragEvent as ReactDragEvent, MutableRefObject, SetStateAction } from 'react'
import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import { runChecklistCreateTransaction, checkChecklistsSubtreeRoom } from '../../services/checklistCounterTransaction'
import { recordWorkspaceActivity } from '../../services/activityService'
import { queueChecklistCanvasProjectionUpsert } from '../../services/checklistCanvasProjectionService'
import { createStripboardRecordDefaults } from '../../utils/stripboardDefaults'
import type { useDashboard } from '../../context/useDashboard'
import { executeChecklistMutation } from '../../services/cascadeEngine'
import { emitTrackEvent } from '../../utils/gamificationEvents'
import type { MockExternalCalendarEvent, TimelineElement } from '../../context/TimelineContext'
import type { useTimeline } from '../../context/useTimeline'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import { buildInitialRecurringOccurrences } from '../../services/recurringChecklistService'
import { canParentHostRecurring, isLegacyRecurringContainerId } from '../../utils/recurringParentEligibility'
import type { ChecklistItem as BoardChecklistItem, TimelineBoardKey } from '../../types/schema'
import { CHECKLIST_CANVAS_LAYOUT_VERSION as WORKSPACE_CANVAS_LAYOUT_VERSION } from '../../types/checklistCanvas'
import type { useWorkspace } from '../../context/useWorkspace'
import type { useAuth } from '../../context/useAuth'
import { generateTimelineGoal } from '../../services/aiService'
import { normalizeChecklistInputLimits, truncateToLimit } from '../../utils/checklistLimits'
import { buildTimelineConstraintContext } from '../../utils/constraintContext'
import { walletService } from '../../services/walletService'
import { invalidateChecklistDefinitionListCache } from '../../services/checklistReadCache'
import { findEmptyCanvasSlot, type CanvasSlotBox } from '../../services/canvasPlacementService'
import {
  GATE_DIAMETER,
  GRID_LINE_HEIGHT,
  NODE_GAP,
  NODE_WIDTH,
} from '../../services/canvasLayoutConstants'
import {
  normalizeWeightage,
  type IngestedNodeDraft,
} from './boardItem'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import { nodesToElementChecklist } from './liveReconciliation'

export function useDraftChecklist({
  checklistLimits,
  recurringLens,
  activeCanvasContainerId,
  item,
  currentUser,
  activeWorkspace,
  currentDisplayName,
  getNextChecklistGridPosition,
  canEditStructure,
  canCreateChecklist,
  showFlash,
  setDraftDrawerNode,
  nodes,
  elements,
  isDebugCanvas,
  commitLocalNodes,
  boardDocumentIdsRef,
  setSelectedNodeId,
  hasElementMirror,
  lastChecklistMirrorRef,
  syncChecklistBookLayoutForType,
  setNodes,
  canEditCanvas,
  collectPlacementBoxes,
  boardRef,
  viewportState,
  boardMetrics,
  clientToWorld,
  setVisionboardChecklistItems,
  activeWorkspaceId,
  getNextFreePosition,
}: {
  checklistLimits: ReturnType<typeof normalizeChecklistInputLimits>
  recurringLens: ReturnType<typeof useRecurringLens>
  activeCanvasContainerId: string
  item: TimelineElement
  currentUser: ReturnType<typeof useAuth>['currentUser']
  activeWorkspace: ReturnType<typeof useWorkspace>['workspaces'][number] | undefined
  currentDisplayName: string
  getNextChecklistGridPosition: (preferred?: { x: number; y: number } | null) => { x: number; y: number }
  canEditStructure: boolean
  canCreateChecklist: boolean
  showFlash: (message: string) => void
  setDraftDrawerNode: Dispatch<SetStateAction<BoardChecklistItem | null>>
  nodes: BoardChecklistItem[]
  elements: TimelineElement[]
  isDebugCanvas: boolean
  commitLocalNodes: (updater: (current: BoardChecklistItem[]) => BoardChecklistItem[]) => void
  boardDocumentIdsRef: MutableRefObject<Set<string>>
  setSelectedNodeId: ReturnType<typeof useDashboard>['setSelectedChecklistNodeId']
  hasElementMirror: boolean
  lastChecklistMirrorRef: MutableRefObject<string>
  syncChecklistBookLayoutForType: (type: 'INDIVIDUAL' | 'DAILY' | 'WEEKLY', nodesForType: readonly BoardChecklistItem[]) => Promise<void>
  setNodes: Dispatch<SetStateAction<BoardChecklistItem[]>>
  canEditCanvas: boolean
  collectPlacementBoxes: (excludeId?: string) => CanvasSlotBox[]
  boardRef: MutableRefObject<HTMLDivElement | null>
  viewportState: ReturnType<typeof useTimeline>['viewportState']
  boardMetrics: { minX: number; minY: number; offsetX: number; offsetY: number; width: number; height: number }
  clientToWorld: (event: { clientX: number; clientY: number }) => { x: number; y: number }
  setVisionboardChecklistItems: Dispatch<SetStateAction<BoardChecklistItem[]>>
  activeWorkspaceId: string | null
  getNextFreePosition: () => { x: number; y: number }
}) {
  const { timeZone } = useWorkspaceDay(item.workspaceId)

  // ---- Popup-first draft flow (P1) -----------------------------------------
  //
  // buildDraftChecklistNode: constructs the in-memory placeholder used by both
  // the direct addNodeAt path (bulk ingest, calendar import, clipboard) and
  // the popup-first path (`startNewDraftChecklist`). Splitting this out means
  // the placeholder shape is defined in exactly one place and stays in lock-
  // step with the eventual Firestore write payload.
  const buildDraftChecklistNode = (
    checklistId: string,
    draft: IngestedNodeDraft = {},
    overrides: { titleOverride?: string } = {},
  ): BoardChecklistItem => {
    const nextTitle = overrides.titleOverride !== undefined
      ? overrides.titleOverride
      : truncateToLimit(draft.title?.trim() || '*EDIT*', checklistLimits.titleMaxLength)
    // Seed occurrences for the rolling window (today + next N-1 days/weeks)
    // when we're under a recurring lens so a freshly-authored DAILY/WEEKLY
    // item lands with the same materialized keys `materializeRecurringWindow`
    // would fill on the next boot. Without this the item has an empty
    // occurrences map and the focused-key filter drops it on the next reload.
    // Non-recurring canvases OMIT the field entirely — spreading
    // `{ occurrences: undefined }` into the Firestore payload triggers
    // "Unsupported field value: undefined" and silently fails the Save.
    const recurringOccurrences = recurringLens
      ? buildInitialRecurringOccurrences(recurringLens.mode, recurringLens.recurringWindowDays)
      : null
    return {
      ...createStripboardRecordDefaults({ boardStatus: 'TODO', priority: Math.ceil(((draft.urgencyStars ?? 1) + (draft.importanceStars ?? 1)) / 2) }),
      id: checklistId,
      parentContainerId: activeCanvasContainerId,
      checklistType: recurringLens?.mode ?? 'INDIVIDUAL',
      ...(recurringOccurrences ? { occurrences: recurringOccurrences } : {}),
      workspaceId: item.workspaceId,
      title: nextTitle,
      description: truncateToLimit(draft.description ?? '', checklistLimits.descriptionMaxLength),
      completionCriteria: '',
      createdById: currentUser?.uid ?? activeWorkspace?.ownerId ?? '',
      createdByName: currentDisplayName || currentUser?.email || 'Workspace admin',
      createdAt: new Date().toISOString(),
      assigneeId: null,
      assigneeName: null,
      updatedAt: new Date().toISOString(),
      lastUpdatedBy: currentUser?.uid ?? null,
      lastUpdatedByName: currentDisplayName || currentUser?.email || null,
      isCompleted: false,
      weightage: Math.min(checklistLimits.weightageMax, normalizeWeightage(draft.weightage ?? 1)),
      position: getNextChecklistGridPosition(),
      workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
      prerequisites: draft.prerequisites ?? [],
      blockedByExternal: null,
      blockedByExternalId: null,
      interWorkspaceTargetId: null,
      isHelperVisible: true,
      dependencyLogicGate: 'ALL_PREREQUISITES_MET',
      hasAutoSpawnTrigger: false,
      autoSpawnTemplates: [],
      requiresProofFile: false,
      proofFileUrl: null,
      proofFileName: null,
      requiresCoSign: false,
      coSignerRoleTarget: 'ADMIN',
      coSignedByUid: null,
      coSignedByName: null,
      isPendingReview: false,
      requiresLocationPin: false,
      targetCoordinates: null,
      verifiedCoordinates: null,
      proofVideoUrl: null,
      countdownHours: null,
      timerStartedAt: null,
      timerExpiredAt: null,
      isUrgentCrisisMode: false,
      enforcePaintSpringDelay: false,
      delayDurationHours: 0,
      commentsCount: 0,
      voiceMemoBlobUrl: null,
      isDescriptionPrivate: false,
      estimatedHours: draft.estimatedHours ?? null,
      actualCost: null,
      complexityStars: 1,
      urgencyStars: 1,
      importanceStars: 1,
      currencyCode: 'USD',
      blockedByNodeIds: [],
      isBlockerForNodeIds: [],
    }
  }

  /** Open the details drawer for a brand-new (unpersisted) checklist item.
   *  Title starts empty — the drawer's isNewNode path requires a non-empty
   *  title before Save is enabled. The item lives only in this component's
   *  state until Save; Discard/close simply drops it. */
  const startNewDraftChecklist = async () => {
    if (!canEditStructure || !canCreateChecklist) return
    const resolvedBoard = (item.board ?? 'DASHBOARD') as TimelineBoardKey
    const roomCheck = await checkChecklistsSubtreeRoom(firestoreDb, {
      workspaceId: item.workspaceId,
      board: resolvedBoard,
      parentContainerId: item.id,
      subtreeSize: 1,
      fallbackLimits: activeWorkspace?.workspaceLimits,
    })
    if (!roomCheck.ok) {
      showFlash(roomCheck.message ?? 'Checklist limit reached.')
      return
    }
    const checklistRef = doc(collection(firestoreDb, 'checklists'))
    const placeholder = buildDraftChecklistNode(checklistRef.id, {}, { titleOverride: '' })
    setDraftDrawerNode(placeholder)
  }

  /** Save handler passed to the draft drawer. Runs the same batch commit as
   *  addNodeAt but with the (possibly-edited) node returned by the drawer,
   *  then closes the drawer. */
  const persistDraftChecklist = async (finalNode: BoardChecklistItem) => {
    const nextNode: BoardChecklistItem = {
      ...finalNode,
      // The drawer may allow title edits; guarantee shape here too.
      title: finalNode.title?.trim() || '*EDIT*',
      updatedAt: new Date().toISOString(),
      lastUpdatedBy: currentUser?.uid ?? null,
      lastUpdatedByName: currentDisplayName || currentUser?.email || null,
    }
    const nextNodes = [...nodes, nextNode]

    // Parent-scoped recurring guard — must run before we touch Firestore.
    // The draft path bypasses createChecklistNative / createDefinition, so
    // this is where we enforce the rule for the in-canvas "New" flow.
    const draftType = (nextNode.checklistType ?? 'INDIVIDUAL') as 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'
    let draftRecurringWindow: { parentElementId: string; startDate: string; endDate: string } | null = null
    if (draftType === 'DAILY' || draftType === 'WEEKLY') {
      if (isLegacyRecurringContainerId(nextNode.parentContainerId ?? '')) {
        showFlash('Top-level recurring canvases are retired — add recurring inside a Task, Goal, or Subtask instead.')
        return
      }
      const parentElement = elements.find((el) => el.id === nextNode.parentContainerId)
      const eligibility = canParentHostRecurring(parentElement ?? null)
      if (!eligibility.ok) {
        showFlash(eligibility.reason)
        return
      }
      draftRecurringWindow = {
        parentElementId: parentElement!.id,
        startDate: (parentElement!.startDate as string).slice(0, 10),
        endDate: (parentElement!.endDate as string).slice(0, 10),
      }
    }

    if (isDebugCanvas) {
      commitLocalNodes((currentNodes) => [...currentNodes, nextNode])
      boardDocumentIdsRef.current.add(nextNode.id)
      setSelectedNodeId(nextNode.id)
      showFlash('Checklist item added.')
      setDraftDrawerNode(null)
      return
    }

    const resolvedBoard = (item.board ?? 'DASHBOARD') as TimelineBoardKey
    const checklistRef = doc(firestoreDb, 'checklists', nextNode.id)
    const persistedPayload = {
      ...nextNode,
      ...(draftRecurringWindow ? {
        recurringWindow: {
          ...draftRecurringWindow,
          syncedAt: serverTimestamp(),
        },
      } : {}),
      sourceChecklistId: checklistRef.id,
      board: resolvedBoard,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    try {
      await runChecklistCreateTransaction(firestoreDb, {
        workspaceId: item.workspaceId,
        board: resolvedBoard,
        parentContainerId: nextNode.parentContainerId ?? item.id,
        checklistRef,
        checklistData: persistedPayload,
        fallbackLimits: activeWorkspace?.workspaceLimits,
        extraWrites: (transaction) => {
          if (hasElementMirror) {
            transaction.update(doc(firestoreDb, 'elements', item.id), {
              checklist: nodesToElementChecklist(item.id, nextNodes),
            })
          }
        },
      })
      const projBatch = writeBatch(firestoreDb)
      queueChecklistCanvasProjectionUpsert(projBatch, firestoreDb, persistedPayload)
      await projBatch.commit().catch(() => {})
    } catch (error) {
      const code = (error as { code?: string })?.code ?? 'unknown'
      console.error('[ChecklistDrawingBoard] draft persist failed', {
        code,
        workspaceId: item.workspaceId,
        parentContainerId: nextNode.parentContainerId,
        error,
      })
      showFlash((error as Error)?.message || 'Could not add checklist item.')
      throw error
    }
    invalidateChecklistDefinitionListCache(item.workspaceId, (nextNode.checklistType ?? 'INDIVIDUAL') as 'INDIVIDUAL' | 'DAILY' | 'WEEKLY')
    if (hasElementMirror) {
      lastChecklistMirrorRef.current = JSON.stringify(nodesToElementChecklist(item.id, nextNodes))
    }
    // Book-model bootstrap: pin the new definition into the current book
    // page immediately so Daily/Weekly items survive page switches.
    try {
      const {
        ensureRecurringBookWindow,
        pageKeyFor,
        setChecklistCompletionOnPage,
      } = await import('../../services/checklistBookService')
      const bookActor = { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || undefined }
      if (bookActor.uid) {
        const type = (nextNode.checklistType ?? 'INDIVIDUAL') as 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'
        const book = await ensureRecurringBookWindow(item.workspaceId, type, bookActor)
        const pageKey = recurringLens && type === recurringLens.mode
          ? recurringLens.focusedKey
          : pageKeyFor(type, new Date(), timeZone)
        if (type === 'INDIVIDUAL') {
          await setChecklistCompletionOnPage(
            book.id,
            pageKey,
            nextNode.id,
            {
              isCompleted: false,
              boardStatus: nextNode.boardStatus ?? null,
              workflowStatus: nextNode.workflowStatus ?? 'CREATED',
            },
            bookActor,
          )
        }
        await syncChecklistBookLayoutForType(type, [nextNode])
      }
    } catch (err) {
      console.debug('[ChecklistDrawingBoard] book bootstrap on create skipped', (err as Error)?.message)
    }
    // Track the just-committed doc id so a subsequent delete (in this same
    // session, before any reload has populated the set) actually removes it
    // from Firestore. Without this, `deleteNode` sees the id is NOT in
    // boardDocumentIdsRef and only strips the node from local state — the
    // item then reappears on the next refresh.
    boardDocumentIdsRef.current.add(nextNode.id)
    setNodes((currentNodes) => [...currentNodes, nextNode])
    setSelectedNodeId(nextNode.id)
    setDraftDrawerNode(null)
    emitTrackEvent('TRACK_CREATE_CHECK')
    void (async () => {
      if (hasElementMirror) {
        await executeChecklistMutation(firestoreDb, checklistRef.id, false)
      }
      await recordWorkspaceActivity(firestoreDb, {
        workspaceId: item.workspaceId,
        actorId: currentUser?.uid,
        actorName: currentDisplayName || currentUser?.email,
        targetType: 'CHECKLIST',
        targetId: checklistRef.id,
        targetTitle: nextNode.title,
        action: 'CHECKLIST_CREATED',
        summary: `Created checklist node ${nextNode.title}`,
        board: item.board,
      }).catch((error) => console.warn('[ChecklistDrawingBoard] activity write failed', error))
    })()
  }

  const addNodeAt = async (position: { x: number; y: number } | null, draft: IngestedNodeDraft = {}) => {
    if (!canEditStructure || !canCreateChecklist) return
    const resolvedBoard = (item.board ?? 'DASHBOARD') as TimelineBoardKey
    const roomCheck = await checkChecklistsSubtreeRoom(firestoreDb, {
      workspaceId: item.workspaceId,
      board: resolvedBoard,
      parentContainerId: activeCanvasContainerId ?? item.id,
      subtreeSize: 1,
      fallbackLimits: activeWorkspace?.workspaceLimits,
    })
    if (!roomCheck.ok) {
      showFlash(roomCheck.message ?? 'Checklist limit reached.')
      return undefined
    }

    // Parent-scoped recurring guard for the drag/paste/auto-add path.
    // Under a recurring lens the type comes from `recurringLens.mode`;
    // it must resolve to a real element parent with both dates set.
    let addNodeRecurringWindow: { parentElementId: string; startDate: string; endDate: string } | null = null
    if (recurringLens) {
      if (isLegacyRecurringContainerId(activeCanvasContainerId ?? '')) {
        showFlash('Top-level recurring canvases are retired — add recurring inside a Task, Goal, or Subtask instead.')
        return
      }
      const parentElement = elements.find((el) => el.id === activeCanvasContainerId)
      const eligibility = canParentHostRecurring(parentElement ?? null)
      if (!eligibility.ok) {
        showFlash(eligibility.reason)
        return
      }
      addNodeRecurringWindow = {
        parentElementId: parentElement!.id,
        startDate: (parentElement!.startDate as string).slice(0, 10),
        endDate: (parentElement!.endDate as string).slice(0, 10),
      }
    }

    const checklistRef = doc(collection(firestoreDb, 'checklists'))
    const nextTitle = truncateToLimit(draft.title?.trim() || '*EDIT*', checklistLimits.titleMaxLength)
    // Same rolling-window seeding as buildDraftChecklistNode — omit the
    // occurrences field entirely on non-recurring canvases so the Firestore
    // payload never carries an `undefined` value.
    const recurringOccurrences = recurringLens
      ? buildInitialRecurringOccurrences(recurringLens.mode, recurringLens.recurringWindowDays)
      : null
    const nextNode: BoardChecklistItem = {
      ...createStripboardRecordDefaults({ boardStatus: 'TODO', priority: Math.ceil(((draft.urgencyStars ?? 1) + (draft.importanceStars ?? 1)) / 2) }),
      id: checklistRef.id,
      parentContainerId: activeCanvasContainerId,
      checklistType: recurringLens?.mode ?? 'INDIVIDUAL',
      ...(recurringOccurrences ? { occurrences: recurringOccurrences } : {}),
      workspaceId: item.workspaceId,
      title: nextTitle,
      description: truncateToLimit(draft.description ?? '', checklistLimits.descriptionMaxLength),
      completionCriteria: '',
      createdById: currentUser?.uid ?? activeWorkspace?.ownerId ?? '',
      createdByName: currentDisplayName || currentUser?.email || 'Workspace admin',
      createdAt: new Date().toISOString(),
      assigneeId: null,
      assigneeName: null,
      updatedAt: new Date().toISOString(),
      lastUpdatedBy: currentUser?.uid ?? null,
      lastUpdatedByName: currentDisplayName || currentUser?.email || null,
      isCompleted: false,
      weightage: Math.min(checklistLimits.weightageMax, normalizeWeightage(draft.weightage ?? 1)),
      position: getNextChecklistGridPosition(position),
      workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
      prerequisites: draft.prerequisites ?? [],
      blockedByExternal: null,
      blockedByExternalId: null,
      interWorkspaceTargetId: null,
      isHelperVisible: true,
      dependencyLogicGate: 'ALL_PREREQUISITES_MET',
      hasAutoSpawnTrigger: false,
      autoSpawnTemplates: [],
      requiresProofFile: false,
      proofFileUrl: null,
      proofFileName: null,
      requiresCoSign: false,
      coSignerRoleTarget: 'ADMIN',
      coSignedByUid: null,
      coSignedByName: null,
      isPendingReview: false,
      requiresLocationPin: false,
      targetCoordinates: null,
      verifiedCoordinates: null,
      proofVideoUrl: null,
      countdownHours: null,
      timerStartedAt: null,
      timerExpiredAt: null,
      isUrgentCrisisMode: false,
      enforcePaintSpringDelay: false,
      delayDurationHours: 0,
      commentsCount: 0,
      voiceMemoBlobUrl: null,
      isDescriptionPrivate: false,
      estimatedHours: draft.estimatedHours ?? null,
      actualCost: null,
      complexityStars: 1,
      urgencyStars: 1,
      importanceStars: 1,
      currencyCode: 'USD',
      blockedByNodeIds: [],
      isBlockerForNodeIds: [],
    }

    const nextNodes = [...nodes, nextNode]
    if (isDebugCanvas) {
      commitLocalNodes((currentNodes) => [...currentNodes, nextNode])
      boardDocumentIdsRef.current.add(checklistRef.id)
      setSelectedNodeId(checklistRef.id)
      showFlash('Checklist item added.')
      return nextNode
    }

    const persistedPayload = {
      ...nextNode,
      ...(addNodeRecurringWindow ? {
        recurringWindow: {
          ...addNodeRecurringWindow,
          syncedAt: serverTimestamp(),
        },
      } : {}),
      sourceChecklistId: checklistRef.id,
      board: resolvedBoard,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    try {
      await runChecklistCreateTransaction(firestoreDb, {
        workspaceId: item.workspaceId,
        board: resolvedBoard,
        parentContainerId: nextNode.parentContainerId ?? item.id,
        checklistRef,
        checklistData: persistedPayload,
        fallbackLimits: activeWorkspace?.workspaceLimits,
        extraWrites: (transaction) => {
          if (hasElementMirror) {
            transaction.update(doc(firestoreDb, 'elements', item.id), {
              checklist: nodesToElementChecklist(item.id, nextNodes),
            })
          }
        },
      })
      const projBatch = writeBatch(firestoreDb)
      queueChecklistCanvasProjectionUpsert(projBatch, firestoreDb, persistedPayload)
      await projBatch.commit().catch(() => {})
    } catch (error) {
      const code = (error as { code?: string })?.code ?? 'unknown'
      console.error('[ChecklistDrawingBoard] checklist node create failed', {
        code,
        workspaceId: item.workspaceId,
        parentContainerId: nextNode.parentContainerId,
        board: resolvedBoard,
        error,
      })
      showFlash((error as Error)?.message || (code === 'permission-denied'
        ? 'You don\'t have permission to add checklist items in this workspace.'
        : 'Could not add checklist item. Try again.'))
      return undefined
    }
    invalidateChecklistDefinitionListCache(item.workspaceId, (nextNode.checklistType ?? 'INDIVIDUAL') as 'INDIVIDUAL' | 'DAILY' | 'WEEKLY')
    if (hasElementMirror) {
      lastChecklistMirrorRef.current = JSON.stringify(nodesToElementChecklist(item.id, nextNodes))
    }
    // Track the just-committed doc id — see persistDraftChecklist above.
    boardDocumentIdsRef.current.add(nextNode.id)
    setNodes((currentNodes) => [...currentNodes, nextNode])
    setSelectedNodeId(checklistRef.id)
    if (hasElementMirror) {
      await executeChecklistMutation(firestoreDb, checklistRef.id, false)
    }
    emitTrackEvent('TRACK_CREATE_CHECK')
    await recordWorkspaceActivity(firestoreDb, {
      workspaceId: item.workspaceId,
      actorId: currentUser?.uid,
      actorName: currentDisplayName || currentUser?.email,
      targetType: 'CHECKLIST',
      targetId: checklistRef.id,
      targetTitle: nextNode.title,
      action: 'CHECKLIST_CREATED',
      summary: `Created checklist node ${nextNode.title}`,
      board: item.board,
    }).catch((error) => console.warn('[ChecklistDrawingBoard] activity write failed', error))
    return nextNode
  }

  /**
   * Add an AND/OR logic gate to the canvas. Gates live in `checklist_items`
   * alongside regular nodes (same load/save plumbing) but are tagged with a
   * `nodeKind` so the renderer can draw them differently and the lock logic
   * can apply gate semantics. Gates start with NO inputs; the editor must
   * wire 2–4 upstream nodes into the gate before they can save the board.
   * Until fully connected the gate shows a delete button.
   */
  const addGateAt = async (
    kind: 'GATE_AND' | 'GATE_OR',
    position: { x: number; y: number },
  ) => {
    if (!canEditCanvas || !canCreateChecklist) return
    const checklistRef = doc(collection(firestoreDb, 'checklists'))
    const title = kind === 'GATE_AND' ? 'AND gate' : 'OR gate'
    // Gates take a distinct hit-box (`GATE_DIAMETER`, not NODE_WIDTH/HEIGHT),
    // so route them through the shared placement resolver with the correct
    // newNode dims. Falls back to the click coord when it fits, otherwise
    // snaps to the nearest empty grid cell — same rule as manual add.
    const gatePosition = findEmptyCanvasSlot({
      existing: collectPlacementBoxes(),
      newNode: { width: GATE_DIAMETER, height: GATE_DIAMETER },
      preferred: { x: Math.max(0, position.x), y: Math.max(0, position.y) },
    })
    const nextNode: BoardChecklistItem = {
      ...createStripboardRecordDefaults({ boardStatus: 'TODO' }),
      id: checklistRef.id,
      parentContainerId: activeCanvasContainerId,
      checklistType: recurringLens?.mode ?? 'INDIVIDUAL',
      ...(recurringLens ? { occurrences: {} } : {}),
      workspaceId: item.workspaceId,
      title,
      description: '',
      completionCriteria: '',
      createdById: currentUser?.uid ?? activeWorkspace?.ownerId ?? '',
      createdByName: currentDisplayName || currentUser?.email || 'Workspace admin',
      createdAt: new Date().toISOString(),
      assigneeId: null,
      assigneeName: null,
      workspaceCanvasLayoutVersion: WORKSPACE_CANVAS_LAYOUT_VERSION,
      isCompleted: false,
      weightage: 1,
      position: gatePosition,
      prerequisites: [],
      blockedByExternal: null,
      blockedByExternalId: null,
      interWorkspaceTargetId: null,
      dependencyLogicGate: 'ALL_PREREQUISITES_MET',
      hasAutoSpawnTrigger: false,
      autoSpawnTemplates: [],
      requiresProofFile: false,
      proofFileUrl: null,
      proofFileName: null,
      requiresCoSign: false,
      coSignerRoleTarget: 'ADMIN',
      coSignedByUid: null,
      coSignedByName: null,
      isPendingReview: false,
      requiresLocationPin: false,
      targetCoordinates: null,
      verifiedCoordinates: null,
      proofVideoUrl: null,
      countdownHours: null,
      timerStartedAt: null,
      timerExpiredAt: null,
      isUrgentCrisisMode: false,
      enforcePaintSpringDelay: false,
      delayDurationHours: 0,
      commentsCount: 0,
      voiceMemoBlobUrl: null,
      isDescriptionPrivate: false,
      estimatedHours: null,
      actualCost: null,
      complexityStars: 1,
      nodeKind: kind,
      gateInputIds: [],
    }
    // Gates use the local-first session model — they show up on the canvas
    // immediately but only persist when the editor presses Save. They go onto
    // the undo stack too via commitLocalNodes.
    commitLocalNodes((current) => [...current, nextNode])
    setSelectedNodeId(null)
    return nextNode
  }

  const calendarEventToNodeDraft = (eventItem: MockExternalCalendarEvent): IngestedNodeDraft => ({
    title: eventItem.title,
    description: eventItem.description ?? `Imported calendar event scheduled for ${eventItem.startDateTime}.`,
    weightage: 1,
  })

  const handleCalendarDrop = (event: ReactDragEvent<HTMLDivElement>) => {
    if (!canEditStructure) return

    // Visionboard → canvas drop: payload identifies one visionboard checklist
    // item; we instantiate a new node here with its title/weightage and pop
    // it off the shelf so it can't be dropped twice.
    const visionboardPayload = event.dataTransfer.getData('application/x-timelining-visionboard-item')
    if (visionboardPayload) {
      try {
        const visionboardItem = JSON.parse(visionboardPayload) as Partial<BoardChecklistItem>
        if (visionboardItem.id && visionboardItem.title) {
          event.preventDefault()
          event.stopPropagation()
          const board = boardRef.current
          const rect = board?.getBoundingClientRect()
          const position = rect
            ? {
              x: event.clientX - rect.left + viewportState.panX - boardMetrics.offsetX,
              y: event.clientY - rect.top + viewportState.panY - boardMetrics.offsetY,
            }
            : clientToWorld(event)
          void addNodeAt(position, {
            title: visionboardItem.title,
            weightage: typeof visionboardItem.weightage === 'number' ? visionboardItem.weightage : 1,
          })
          setVisionboardChecklistItems((current) => current.filter((candidate) => candidate.id !== visionboardItem.id))
          return
        }
      } catch {
        // fall through to calendar handler below
      }
    }

    const payload =
      event.dataTransfer.getData('application/json') ||
      event.dataTransfer.getData('application/x-timelining-calendar-event') ||
      event.dataTransfer.getData('text/plain')
    if (!payload) return

    try {
      const eventItem = JSON.parse(payload) as Partial<MockExternalCalendarEvent>
      if (!eventItem.id || !eventItem.title || !eventItem.startDateTime) return
      event.preventDefault()
      event.stopPropagation()
      const board = boardRef.current
      const rect = board?.getBoundingClientRect()
      const position = rect
        ? {
          x: event.clientX - rect.left + viewportState.panX - boardMetrics.offsetX,
          y: event.clientY - rect.top + viewportState.panY - boardMetrics.offsetY,
        }
        : clientToWorld(event)
      void addNodeAt(position, calendarEventToNodeDraft(eventItem as MockExternalCalendarEvent))
        .then(() => showFlash('Calendar event ingested into canvas.'))
    } catch {
      // Ignore non-calendar drag payloads.
    }
  }

  const extractLocalClipboardNodes = (clipboardText: string): IngestedNodeDraft[] => {
    const lines = clipboardText
      .split(/\r?\n|[•;]+/g)
      .map((line) => line.replace(/^[-*\d.)\s]+/, '').trim())
      .filter((line) => line.length > 0)

    const sourceLines = lines.length > 0 ? lines : [clipboardText.trim()]
    return sourceLines.slice(0, 6).map((line) => ({
      title: truncateToLimit(line, checklistLimits.titleMaxLength),
      description: 'Extracted from clipboard workflow notes.',
      weightage: /\b(critical|urgent|deadline|blocker)\b/i.test(line) ? 3 : 1,
    }))
  }

  const handleClipboardBlueprintExtract = async (clipboardText: string) => {
    if (!canEditStructure) return

    let drafts: IngestedNodeDraft[]
    let walletReservationId: string | null = null
    try {
      if (!currentUser || currentUser.isAnonymous) {
        throw new Error('Sign in with a non-anonymous account to use AI extraction.')
      }
      const reservation = await walletService.reserveCredits(activeWorkspaceId || item.workspaceId, 25, { purpose: 'GENERATION', targetId: item.id })
      walletReservationId = reservation.reservationId
      const generatedGoal = await generateTimelineGoal(
        currentUser.uid,
        `Extract concise checklist canvas nodes from these workflow notes. Keep titles operational and dependency-ready:\n\n${clipboardText}`,
        buildTimelineConstraintContext(elements),
      )
      await walletService.commitCredits(activeWorkspaceId || item.workspaceId, walletReservationId, 25)
      drafts = [
        ...generatedGoal.checklist.map((check) => ({
          title: check.text,
          description: 'Gemini extracted checklist item from clipboard notes.',
          weightage: check.weightage,
        })),
        ...generatedGoal.tasks.flatMap((task) => [
          {
            title: task.title,
            description: task.description,
            weightage: 2,
          },
          ...task.checklist.map((check) => ({
            title: check.text,
            description: `Gemini extracted under ${task.title}.`,
            weightage: check.weightage,
          })),
        ]),
      ].slice(0, 8)
    } catch (error) {
      if (walletReservationId) await walletService.releaseCredits(activeWorkspaceId || item.workspaceId, walletReservationId).catch(() => undefined)
      console.warn('[ChecklistDrawingBoard] Gemini clipboard extraction failed; using local parser fallback.', error)
      drafts = extractLocalClipboardNodes(clipboardText)
    }

    if (drafts.length === 0) return
    const basePosition = getNextFreePosition()
    let previousNodeId: string | null = null
    for (const [index, draft] of drafts.entries()) {
      const createdNode = await addNodeAt({
        x: basePosition.x + ((index % 2) * (NODE_WIDTH + NODE_GAP)),
        y: basePosition.y + (Math.floor(index / 2) * GRID_LINE_HEIGHT),
      }, {
        ...draft,
        prerequisites: previousNodeId ? [previousNodeId] : [],
      })
      previousNodeId = createdNode?.id ?? previousNodeId
    }
    showFlash(`Extracted ${drafts.length} linked clipboard node${drafts.length === 1 ? '' : 's'}.`)
  }

  return {
    buildDraftChecklistNode,
    startNewDraftChecklist,
    persistDraftChecklist,
    addNodeAt,
    addGateAt,
    calendarEventToNodeDraft,
    handleCalendarDrop,
    extractLocalClipboardNodes,
    handleClipboardBlueprintExtract,
  }
}
